import { Injectable, inject } from '@angular/core';
import { ClusterService } from './cluster.service';
import { OidcService } from './auth/oidc.service';
import { LOCALE_ID } from '@angular/core';

import { ApiError } from './utils/errors';

/** @deprecated Use ApiError directly. Kept for backwards compatibility. */
export class ErrorWithDetails extends ApiError {
    constructor(message: string, details?: string) {
        super(message, { details });
        this.name = 'ErrorWithDetails';
    }
}

export interface Response<T> {
    state: 'error' | 'ok' | 'empty' | 'loading';
    error?: unknown;
    data: T | null;
}

export interface OKResponse<T>  extends Response<T> {
    state: 'ok';
    data: T;
}

export interface ErrorResponse<T> extends Response<T> {
    state: 'error';
    error: unknown;
}

export function isOKResponse<T>(response: Response<T>): response is OKResponse<T> {
    return response.state === 'ok';
}

export function isErrorResponse<T>(response: Response<T>): response is ErrorResponse<T> {
    return response.state === 'error';
}

@Injectable({
    providedIn: 'root'
})
export class Api {


    private oidc = inject(OidcService);
    private clusterService = inject(ClusterService);
    private locale = inject(LOCALE_ID);

    async get<T>(path: string) {
        return this.call<T>('GET', path);
    }

    async getRaw<T>(path: string) {
        return this.rawCall<T>('GET', path);
    }



    async post<T>(path: string, body?: any) {
        return this.call<T>('POST', path, body);
    }

    async put<T>(path: string, body?: any) {
        return this.call<T>('PUT', path, body);
    }

    async delete<T>(path: string) {
        return this.call<T>('DELETE', path);
    }

    private async _call<T>(method: string, path: string, body?: any, retried?: boolean): Promise<T> {
        const apiInfo = this.oidc.apiInfo();

        const urls = this.clusterService.getCurrentUrls();
        const response = await fetch(urls.api + path, {
            method: method,
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': apiInfo.token,
                'EAS-Organisation': apiInfo.organisation || '',
                'EAS-Username': apiInfo.username || '',
                'Accept-Language': this.locale,
            },
        });

        const data = await response.json();
        if (response.status === 401) {
            if (retried) {
                throw new Error('Unauthorized');
            }

            if (data.message && data.message.includes('stepup')) {
                await this.oidc.stepUp();
            } else {
                await this.oidc.renewToken();
            }
            return this._call<T>(method, path, body, true);
        }

        if (!response.ok) {
            throw new ErrorWithDetails(data.message || 'Unknown error', data.details);
        }
        return data;
    }

    async rawCall<T>(method: string, path: string, body?: any): Promise<T> {
        return this._call<T>(method, path, body);
    }

    async call<T>(method: string, path: string, body?: any): Promise<Response<T>> {
        try {
            const data = await this._call<T>(method, path, body);
            return {
                state: 'ok',
                data: data
            }
        } catch (error) {
            return {
                state: 'error',
                error: error,
                data: null
            }
        }
    }
}
